/**
 * AdminMenuBuilderPage — strona /menu-builder w admin scope (ADR-0034).
 *
 * Cienki wrapper nad `MenuBuilderShell` (wyciągniętym z tenant scope w Commit 1)
 * z admin providerami:
 *   - apiPrefix `/admin/menu`
 *   - i18nPrefix `admin.menuLayoutBuilder`
 *   - query keys `['admin', 'menu', ...]`
 *   - useAdminMenuLayouts / useAdminMenuSource / useAdminLayoutRoleAssignments
 *   - ADMIN_ROLES static rolesQuery (4-value enum zamiast role templates)
 *   - ADMIN_SYSTEM_ITEMS systemItemDefinitions (16 items)
 *   - allowModuleType=false (admin nie ma subskrypcji modułów)
 *   - backUrl `/dashboard`
 *
 * Defense-in-depth — TRZY warstwy gate'u:
 *   1. Sidebar link ukryty bez `admin_menu.manage` (config/menu.ts requiredRoles — legacy
 *      rola, poza zakresem tej migracji).
 *   2. Page-level guard (TEN plik) — brak `admin_menu.manage` → redirect.
 *   3. Backend endpointy /api/admin/menu/* — `requirePermission('admin_menu.manage')`.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { ADMIN_ROLES, ADMIN_SYSTEM_ITEMS, type AdminSystemItemDefinition } from '@saas/shared';

import { Layout } from '@/components/Layout';
import { type RoleOption, type RolesQueryLike } from '@/components/menu/LayoutRolesModal';
import {
  MenuBuilderShell,
  type MenuBuilderShellQueryKeys,
} from '@/components/menu-builder-shell/MenuBuilderShell';
import { useAuth } from '@/contexts/AuthContext';
import { ACTIVE_ADMIN_MENU_LAYOUT_QUERY_KEY } from '@/hooks/admin-menu/useActiveAdminMenuLayout';
import {
  ADMIN_LAYOUT_ROLE_ASSIGNMENTS_KEY,
  useAdminLayoutRoleAssignments,
} from '@/hooks/admin-menu/useAdminLayoutRoleAssignments';
import {
  ADMIN_MENU_LAYOUTS_QUERY_KEY,
  useAdminMenuLayouts,
} from '@/hooks/admin-menu/useAdminMenuLayouts';
import { AVAILABLE_ADMIN_MENU_LAYOUTS_KEY } from '@/hooks/admin-menu/useAdminMenuPreference';
import { useAdminMenuSource } from '@/hooks/admin-menu/useAdminMenuSource';

const ADMIN_QUERY_KEYS: MenuBuilderShellQueryKeys = {
  activeLayout: ACTIVE_ADMIN_MENU_LAYOUT_QUERY_KEY,
  layoutsList: ADMIN_MENU_LAYOUTS_QUERY_KEY,
  availableLayouts: AVAILABLE_ADMIN_MENU_LAYOUTS_KEY,
  layoutById: (id) => ['admin', 'menu', 'layouts', id] as const,
  roleAssignments: ADMIN_LAYOUT_ROLE_ASSIGNMENTS_KEY,
};

const ADMIN_SYSTEM_ITEMS_MAP: ReadonlyMap<string, AdminSystemItemDefinition> = new Map(
  ADMIN_SYSTEM_ITEMS.map((s) => [s.itemKey as string, s]),
);

export function AdminMenuBuilderPage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();

  // Static rolesQuery — admin scope nie ma role templates (jak tenant), tylko
  // 4-value enum ADMIN_ROLES. Buduje obiekt zgodny z RolesQueryLike (data + isLoading),
  // bo modal akceptuje strukturalny shape (nie wymaga prawdziwego UseQueryResult).
  // useMemo żeby referencja była stabilna między renderami (modal useEffect deps).
  // MUSI być przed early return (Rules of Hooks — hooks zawsze w tej samej kolejności).
  const rolesQuery = useMemo<RolesQueryLike>(
    () => ({
      data: {
        items: ADMIN_ROLES.map<RoleOption>((role) => ({
          id: role,
          label: t(`admin.menuLayoutBuilder.roles.${role}`),
        })),
      },
      isLoading: false,
    }),
    [t],
  );

  // Page-level permission gate (ADR-0034 — warstwa #2 z trzech: sidebar filter / page guard /
  // backend 403). Strict guard — brak admina też redirectuje, żeby warstwa #2 działała
  // samodzielnie nawet gdy upstream `ProtectedRoute` (warstwa #1) zawiedzie.
  if (!hasPermission('admin_menu.manage')) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <MenuBuilderShell
        apiPrefix="/admin/menu"
        i18nPrefix="admin.menuLayoutBuilder"
        backUrl="/dashboard"
        heightClassName="h-[calc(100vh-6.5rem)]"
        allowModuleType={false}
        queryKeys={ADMIN_QUERY_KEYS}
        useLayoutsHook={useAdminMenuLayouts}
        useSourceHook={useAdminMenuSource}
        useAssignmentsHook={useAdminLayoutRoleAssignments}
        rolesQuery={rolesQuery}
        systemItemDefinitions={ADMIN_SYSTEM_ITEMS_MAP}
      />
    </Layout>
  );
}
